/* eslint-disable jira/import-whitelist */
import { FetchError } from '@jira/platform__early-script-utils/src/types.tsx';
import type {
	AIContextServicePanelResourceResponse,
	DraftReplyNextStepInlineAction,
} from './ContextPanelServiceTypes.tsx';
import { parseAdf, transformContent } from './transformContent.tsx';
import type { graphqlContextPanelQuery$data as AIContextPanelGraphQLResponse } from './__generated__/graphqlContextPanelQuery.graphql.ts';

type SuggestedNextStepsInput = NonNullable<
	Extract<
		AIContextPanelGraphQLResponse['agentAI_contextPanel'],
		{ __typename: 'AgentAIContextPanelResponse' }
	>['suggestedNextSteps']
>;

type NextStepInput = NonNullable<NonNullable<SuggestedNextStepsInput['nextSteps']>[number]>;

const mapGraphQlInlineAction = (
	inlineAction: NextStepInput['inlineAction'],
): DraftReplyNextStepInlineAction | undefined => {
	if (!inlineAction || inlineAction.type !== 'DRAFT_REPLY' || !inlineAction.draftReplyPill) {
		return undefined;
	}
	const pill = inlineAction.draftReplyPill;
	return {
		type: 'DRAFT_REPLY',
		draftReplyPill: {
			pillId: pill.pillId,
			text: pill.text,
			category: pill.category,
			...(pill.intent != null && pill.intent !== '' ? { intent: pill.intent } : {}),
			...(pill.goal != null && pill.goal !== '' ? { goal: pill.goal } : {}),
			...(pill.triggerSummary != null && pill.triggerSummary !== ''
				? { triggerSummary: pill.triggerSummary }
				: {}),
			...(pill.example != null && pill.example !== '' ? { example: pill.example } : {}),
		},
	};
};

const transformSuggestedNextSteps = (
	suggestedNextSteps: SuggestedNextStepsInput | null | undefined,
) => {
	if (!suggestedNextSteps?.citations?.length && !suggestedNextSteps?.nextSteps?.length) {
		return undefined;
	}
	return {
		citations: (suggestedNextSteps?.citations ?? []).flatMap((c) =>
			c != null
				? [
						{
							id: c.id,
							sourceType: c.sourceType,
							sourceId: c.sourceId ?? null,
							sourceUrl: c.sourceUrl,
							title: c.title,
						},
					]
				: [],
		),
		nextSteps: (suggestedNextSteps?.nextSteps ?? []).flatMap((step) => {
			const content = parseAdf(step?.content);
			if (content == null) {
				return [];
			}
			const normalizedInlineAction = mapGraphQlInlineAction(step?.inlineAction ?? null);
			return [
				{
					content,
					...(normalizedInlineAction != null ? { inlineAction: normalizedInlineAction } : {}),
				},
			];
		}),
	};
};

export const transformGraphQlResponse = (
	graphQLResponse: AIContextPanelGraphQLResponse | undefined,
	traceIds?: string[],
): AIContextServicePanelResourceResponse => {
	const data =
		graphQLResponse?.agentAI_contextPanel?.__typename === 'AgentAIContextPanelResponse'
			? graphQLResponse?.agentAI_contextPanel
			: null;
	return {
		type: 'ServicePanelSuggestions',
		traceIds,
		summary: parseAdf(data?.summary),
		reporterDetails: parseAdf(data?.reporterDetails),
		agentSteps: parseAdf(data?.agentSteps),
		suggestedNextSteps: transformSuggestedNextSteps(data?.suggestedNextSteps),
		suggestedActions: data?.suggestedActions
			? // oxlint-disable-next-line @typescript-eslint/no-explicit-any
				data?.suggestedActions?.map((action: any) => ({
					type: action.type,
					context: {
						...action.context,
						suggestion: action.context?.suggestion,
					},
					content: transformContent(action?.content),
				}))
			: [],
	};
};

export const handleGraphQLresponse = (
	graphQLResponse: AIContextPanelGraphQLResponse | undefined,
	traceIds?: string[],
): AIContextServicePanelResourceResponse => {
	const responseType = graphQLResponse?.agentAI_contextPanel?.__typename;
	if (responseType === 'AgentAIContextPanelResponse') {
		return transformGraphQlResponse(graphQLResponse, traceIds);
	}
	if (responseType === 'QueryError') {
		const errorMessage = graphQLResponse?.agentAI_contextPanel?.message ?? 'Unknown error';
		const errorStatus = graphQLResponse?.agentAI_contextPanel?.extensions?.[0]?.statusCode ?? 500;
		throw new FetchError(
			errorStatus,
			`Error processing graphQL query: ${errorMessage}`,
			traceIds?.[0],
		);
	}
	throw new FetchError(500, 'Failed to fetch data from AGG', traceIds?.[0]);
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const handleGraphQLError = (error: any, traceIds?: string[]): never => {
	throw new FetchError(500, `Failed to fetch data from AGG : ${error?.message}`, traceIds?.[0]);
};
